
import noteAPI from '@/api/notes';
import hasCurrentFirmUuid from '@/mixins/hasCurrentFirmUuid';
import checkForErrors from '@/helpers/checkForErrors';
import { mapGetters, mapState } from 'vuex';
import { debounce } from 'debounce';
import hasStickyData from '@/mixins/hasStickyData';
import { getFiltersForPath, pathsRelatedToFactFindSections } from '@/helpers/notePathHelper';
import noteFilters from '@/data/filters/noteFilters';

export default {
  name: 'Notes',
  layout: 'simplified',
  middleware: 'auth',

  mixins: [hasCurrentFirmUuid, hasStickyData],

  props: {
    inModal: {
      type: Boolean,
      default: false,
    },
    notable_type: {
      type: String,
      required: true,
    },
    forClient: {
      type: Boolean,
      default: false,
    },
    notable_uuid: {
      type: String,
      required: true,
    },
    path: {
      type: String,
      required: false,
    },
    editAction: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      addingNote: false,
      focusState: false,
      form: {
        contents: '',
        notable_type: null,
        notable_uuid: null,
        type: 'note',
        is_pinned: false,
        visible_to_clients: false,
      },
      nonGroupedNotes: [],
      meta: {},
      show: false,
      hoveringId: null,
      editing: {},
      myInterval: null,
      contents: '',
      isPinnedNote: false,
    };
  },

  computed: {
    ...mapState('user', ['currentLogin']),
    ...mapState('client', ['client']),
    ...mapState('circle', ['circle']),
    ...mapGetters('user', ['isClient']),

    pageFilter() {
      return noteFilters;
    },
    pathFilters() {
      if (this.path) {
        return getFiltersForPath(this.path);
      }
      return null;
    },

    showVisibleToggle() {
      if (pathsRelatedToFactFindSections.includes(this.path)) {
        return true;
      }
      return false;
    },
  },

  mounted() {
    this.loadNoteContents();
  },

  methods: {
    async loadNoteContents() {
      // look in indexDB to see if there's a previously saved note that wasn't pushed to the API
      let note = await this.getStickyData(this.notable_type + '-' + this.notable_uuid);
      if (note && note.contents !== null) {
        this.contents = note.contents;
        this.form.contents = note.contents;
        this.form.type = note.note;
        this.focusState = true;
      }
    },

    setFocusMode() {
      this.focusState = true;
    },

    applyFilters() {
      if (this.$refs.notefilter) {
        this.$refs.notefilter.refresh();
      }
    },

    gotResults(results) {
      this.nonGroupedNotes = results.data;
      this.meta = results.meta;
    },

    async runFocusOut() {
      if (this.contents.length !== 0) {
        this.$nuxt.$emit('confirm', {
          message: `Are you sure you would like to cancel this note?`,
          callback: async () => {
            this.$refs.redactor.clearContents();
            await this.setStickyData(this.notable_type + '-' + this.notable_uuid, { contents: null });
            this.focusState = false;
          },
        });
      }
    },

    changePage(url) {
      if (url === null) {
        return;
      }
      this.loaded = false;
      this.$nuxt.$axios.get(url).then((rsp) => {
        this.nonGroupedNotes = rsp.data.data;
        this.meta = rsp.data.meta;
        this.loaded = true;
      });
    },

    async addNote() {
      this.addingNote = true;
      this.form.contents = this.contents;
      this.form.notable_type = this.notable_type;
      this.form.notable_uuid = this.notable_uuid;
      this.form.is_pinned = this.isPinnedNote;

      if (this.isClient) {
        // will get stripped from payload
        this.form.visible_to_clients = undefined;
      }

      if (this.path) {
        this.form.path = this.path;
      }

      noteAPI
        .postNotes(this.form)
        .then(async (rsp) => {
          await this.removeData(this.notable_type + '-' + this.notable_uuid);
          await this.applyFilters();

          this.focusState = false;
          this.contents = '';
          this.form.type = 'note';
          this.form.is_pinned = false;
          this.isPinnedNote = false;
          this.$refs.redactor.clearContents();
          this.$nuxt.$emit('notification', 'Note saved');
          this.$nuxt.$emit('refreshNotesTotal');
        })
        .catch((err) => {
          checkForErrors.process(err, this.$refs.formErrorPanel);
        })
        .finally(() => {
          this.addingNote = false;
        });
    },

    showDelete: function (e) {
      e.target.children[2].classList.remove('hidden');
      e.target.children[2].classList.add('block');
      this.hoveringId = e.target.id;
      e.target.classList.add('bg-p-gray-8');
    },

    startEditNote(note) {
      if (this.isClient || this.editAction === 'emit') {
        this.$nuxt.$emit('editNote', note);
      } else {
        this.editing = note;
        this.$refs.edit.open();
      }
    },
  },

  watch: {
    contents: debounce(async function () {
      if (this.contents && this.contents !== '<p></p>' && !this.addingNote) {
        let newNote = {
          contents: this.contents,
          notable_type: this.notable_type,
          notable_uuid: this.notable_uuid,
          note: this.form.type,
        };
        await this.setStickyData(this.notable_type + '-' + this.notable_uuid, newNote);
      }
    }, 2000),
  },
};
